import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SubscriptionResponse } from '@dynamika-shared/types/subscription';
import { NotificationRecipientType } from '@restore/clinicalTrialNotificationHistory/clinicalTrialNotificationHistory.types';
import {
  ClinicalTrialSubscriptionState,
  FetchAllSubscriptionsForTypeAction,
  FetchAllSubscriptionsForTypeSuccessAction,
  FetchNotificationCountsAction,
  FetchNotificationCountsSuccessAction,
  FetchSubscriptionsAction,
  FetchSubscriptionsSuccessAction,
  DeleteSubscriptionAction,
  DeleteSubscriptionSuccessAction,
  UpdateSubscriptionAction,
  UpdateSubscriptionSuccessAction,
  CreateSubscriptionAction,
  CreateSubscriptionSuccessAction,
  DeleteSubscriptionByIdAction,
  EditSubscriptionByIdAction,
} from './clinicalTrialSubscription.types';
import { createSliceHook, makeActionCreator } from '../helpers';
import { subscriptionFiltersSlice } from './subscriptionFilters.slice';

const emptySubscriptionsResponse: SubscriptionResponse = {
  content: [],
  pageable: {
    pageNumber: 0,
    pageSize: 10,
    sort: [],
    offset: 0,
    paged: true,
    unpaged: false,
  },
  last: true,
  totalPages: 0,
  totalElements: 0,
  size: 10,
  number: 0,
  sort: [],
  first: true,
  numberOfElements: 0,
  empty: true,
};

const initialState: ClinicalTrialSubscriptionState = {
  countLoading: false,
  countError: undefined,
  notificationsCount: undefined,
  subscriptionsLoading: false,
  subscriptionsError: undefined,
  subscriptions: emptySubscriptionsResponse,
  allSubscriptionsForType: [],
  filters: subscriptionFiltersSlice.getInitialState(),
};

const { actResolve, actReject, actReq, actEmpty } =
  makeActionCreator<ClinicalTrialSubscriptionState>();

export const clinicalTrialSubscriptionSlice = createSlice({
  name: 'clinicalTrialSubscription',
  initialState,
  reducers: {
    fetchNotificationCounts:
      actReq<FetchNotificationCountsAction['payload']>('countLoading'),
    fetchNotificationCountsSuccess: actResolve<
      FetchNotificationCountsSuccessAction['payload']
    >('notificationsCount', 'countLoading', 'countError'),
    fetchNotificationCountsFailure: actReject('countError', 'countLoading'),

    fetchSubscriptions: actReq<FetchSubscriptionsAction['payload']>(
      'subscriptionsLoading'
    ),
    fetchSubscriptionsSuccess: actResolve<
      FetchSubscriptionsSuccessAction['payload']
    >('subscriptions', 'subscriptionsLoading', 'subscriptionsError'),
    fetchSubscriptionsFailure: actReject(
      'subscriptionsError',
      'subscriptionsLoading'
    ),

    // No loading flag — this fetch is a background lookup used only by the
    // Add/Edit modal to validate uniqueness; it should not block the table.
    fetchAllSubscriptionsForType:
      actEmpty<FetchAllSubscriptionsForTypeAction['payload']>(),
    fetchAllSubscriptionsForTypeSuccess: (
      state,
      action: FetchAllSubscriptionsForTypeSuccessAction
    ) => {
      state.allSubscriptionsForType = action.payload;
    },
    fetchAllSubscriptionsForTypeFailure: state => {
      state.allSubscriptionsForType = [];
    },

    deleteSubscription: actReq<DeleteSubscriptionAction['payload']>(
      'subscriptionsLoading'
    ),
    deleteSubscriptionSuccess: (
      state,
      action: PayloadAction<DeleteSubscriptionSuccessAction['payload']>
    ) => {
      state.subscriptionsLoading = false;
      state.subscriptionsError = undefined;
      state.subscriptions.content = state.subscriptions.content.map(
        subscription =>
          action.payload.subscriptionIds.includes(subscription.subscriptionId)
            ? { ...subscription, subscribed: false }
            : subscription
      );
    },
    deleteSubscriptionFailure: actReject(
      'subscriptionsError',
      'subscriptionsLoading'
    ),

    updateSubscription: actReq<UpdateSubscriptionAction['payload']>(
      'subscriptionsLoading'
    ),
    updateSubscriptionSuccess: (
      state,
      action: PayloadAction<UpdateSubscriptionSuccessAction['payload']>
    ) => {
      state.subscriptionsLoading = false;
      state.subscriptionsError = undefined;
      state.subscriptions.content = state.subscriptions.content.map(
        subscription =>
          action.payload.subscriptionIds.includes(subscription.subscriptionId)
            ? { ...subscription, subscribed: true }
            : subscription
      );
    },
    updateSubscriptionFailure: actReject<Error | null>(
      'subscriptionsError',
      'subscriptionsLoading'
    ),
    createSubscription: actReq<CreateSubscriptionAction['payload']>(
      'subscriptionsLoading'
    ),
    createSubscriptionSuccess: (
      state,
      action: PayloadAction<CreateSubscriptionSuccessAction['payload']>
    ) => {
      state.subscriptionsLoading = false;
      state.subscriptionsError = undefined;
      const subscription = action.payload.subscription;
      const filter = subscription.filter as { type?: string; sites?: string[] } | undefined;
      const newSubscriptionContent = {
        channelIdentifier: subscription.channelUserIdentifier,
        clinicalTrialId: subscription.clinicalTrialId,
        fullName: subscription.user.fullName,
        scope: filter?.type || '',
        subscribed: subscription.subscribed,
        subscriptionId: subscription.id,
        subscriptionType: subscription.userId
          ? NotificationRecipientType.USER
          : NotificationRecipientType.EMAIL,
        userId: subscription.userId,
      };
      state.subscriptions = {
        ...state.subscriptions,
        content: [...state.subscriptions.content, newSubscriptionContent],
      };
      state.allSubscriptionsForType = [
        ...state.allSubscriptionsForType,
        newSubscriptionContent,
      ];
    },
    createSubscriptionFailure: actReject(
      'subscriptionsError',
      'subscriptionsLoading'
    ),
    deleteSubscriptionById: actReq<DeleteSubscriptionByIdAction['payload']>(
      'subscriptionsLoading'
    ),
    deleteSubscriptionByIdSuccess: state => {
      state.subscriptionsLoading = false;
      state.subscriptionsError = undefined;
    },
    deleteSubscriptionByIdFailure: actReject(
      'subscriptionsError',
      'subscriptionsLoading'
    ),
    editSubscriptionById: actReq<EditSubscriptionByIdAction['payload']>(
      'subscriptionsLoading'
    ),
    editSubscriptionByIdSuccess: state => {
      state.subscriptionsLoading = false;
      state.subscriptionsError = undefined;
    },
    editSubscriptionByIdFailure: actReject(
      'subscriptionsError',
      'subscriptionsLoading'
    ),

    toggleActiveFilters: (state, action) => {
      state.filters = subscriptionFiltersSlice.reducer(
        state.filters,
        subscriptionFiltersSlice.actions.toggleActiveFilters(action.payload)
      );
    },
    toggleSelectedFilters: (state, action) => {
      state.filters = subscriptionFiltersSlice.reducer(
        state.filters,
        subscriptionFiltersSlice.actions.toggleSelectedFilters(action.payload)
      );
    },
    setAllFilters: (state, action) => {
      state.filters = subscriptionFiltersSlice.reducer(
        state.filters,
        subscriptionFiltersSlice.actions.setAllFilters(action.payload)
      );
    },
    resetFilters: state => {
      state.filters = subscriptionFiltersSlice.reducer(
        state.filters,
        subscriptionFiltersSlice.actions.reset()
      );
    },
  },
});

export const {
  fetchNotificationCounts,
  fetchNotificationCountsSuccess,
  fetchNotificationCountsFailure,
  fetchSubscriptions,
  fetchSubscriptionsSuccess,
  fetchSubscriptionsFailure,
  updateSubscription,
  updateSubscriptionSuccess,
  updateSubscriptionFailure,
  deleteSubscription,
  deleteSubscriptionSuccess,
  deleteSubscriptionFailure,
  toggleActiveFilters,
  toggleSelectedFilters,
  setAllFilters,
  resetFilters,
} = clinicalTrialSubscriptionSlice.actions;

export const actions = {
  ...clinicalTrialSubscriptionSlice.actions,
};

export const useClinicalTrialSubscriptionSlice = createSliceHook(
  clinicalTrialSubscriptionSlice.name,
  actions
);
